import React, { useState } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Label } from '@atlaskit/form';
import InfoPanel from '@atlaskit/icon/core/migration/information--editor-panel';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useShowErrorFlag } from '@atlassian/jira-personal-settings-error-flag/src/ui/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceField$key } from '@atlassian/jira-relay/src/__generated__/enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceField.graphql';
import type { enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceFieldMutation } from '@atlassian/jira-relay/src/__generated__/enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceFieldMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import messages from './messages.tsx';

export const EnableBatchingPreferenceField = ({
	enableBatchingFragment,
}: {
	enableBatchingFragment: enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceField$key;
}) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { showErrorFlag } = useShowErrorFlag();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
	const data = useFragment(
		graphql`
			fragment enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceField on JiraNotificationOptions {
				batchWindow @required(action: THROW)
				id @required(action: THROW)
			}
		`,
		enableBatchingFragment,
	);

	const [commit] =
		useMutation<enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceFieldMutation>(
			graphql`
				mutation enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceFieldMutation(
					$cloudId: ID!
					$input: JiraUpdateNotificationOptionsInput!
				) {
					jira {
						updateGlobalNotificationOptions(cloudId: $cloudId, input: $input)
							@optIn(to: "JiraNotificationPreference") {
							success
							errors {
								message
							}
							options {
								...enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceField
							}
						}
					}
				}
			`,
		);

	return (
		<Inline spread="space-between" alignBlock="center">
			<Label htmlFor="enable-batching">
				<Inline alignBlock="center" xcss={labelHolderStyles}>
					<Inline
						testId="personal-settings-preference-fields.common.ui.enable-batching.toggle"
						xcss={labelStyles}
						alignBlock="center"
					>
						{formatMessage(messages.toggleLabel)}
					</Inline>
					<Popup
						placement="right-start"
						messageId="personal-settings-preference-fields.common.ui.enable-batching.popup"
						messageType="transactional"
						isOpen={isInfoPanelOpen}
						onClose={() => {
							setIsInfoPanelOpen(false);
						}}
						content={() => (
							<Box xcss={infoDialogStyles}>
								{formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.infoDialogContentIssueTermRefresh
										: messages.infoDialogContent,
								)}
							</Box>
						)}
						trigger={({
							ref,
							'aria-haspopup': ariaHasPopup,
							'aria-controls': ariaControls,
							'data-ds--level': dataDSLevel,
						}) => (
							<Box
								xcss={infoIconStyles}
								onMouseEnter={() => {
									setIsInfoPanelOpen(true);
								}}
								onMouseLeave={() => {
									setIsInfoPanelOpen(false);
								}}
								testId="personal-settings-preference-fields.common.ui.enable-batching.info-pop-up-button-container"
								ref={ref}
								aria-haspopup={ariaHasPopup}
								aria-controls={ariaControls}
								data-ds--level={dataDSLevel}
							>
								<InfoPanel
									label={formatMessage(messages.infoPanelTriggerLabel)}
									color={token('color.icon')}
								/>
							</Box>
						)}
					/>
				</Inline>
			</Label>
			<Toggle
				id="enable-batching"
				isChecked={data.batchWindow !== 'NO_BATCHING'}
				onChange={(option) => {
					if (option) {
						const isEnabled = option.currentTarget.checked;
						commit({
							variables: {
								cloudId,
								input: {
									batchWindow: option.currentTarget.checked ? 'DEFAULT_BATCHING' : 'NO_BATCHING',
								},
							},
							optimisticResponse: {
								jira: {
									updateGlobalNotificationOptions: {
										success: true,
										errors: [],
										options: {
											id: data.id,
											batchWindow: option.currentTarget.checked
												? 'DEFAULT_BATCHING'
												: 'NO_BATCHING',
										},
									},
								},
							},
							onError: (error) => {
								showErrorFlag();
								fireErrorAnalytics({
									meta: {
										id: 'mutateBatchWindowPreference',
										packageName: 'jiraPersonalSettingsPreferenceFields',
										teamName: 'jira-notifications-platform',
									},
									error,
									sendToPrivacyUnsafeSplunk: true,
								});
							},
							onCompleted: (response) => {
								const errors = response?.jira?.updateGlobalNotificationOptions?.errors;
								if (errors && errors?.length > 0) {
									showErrorFlag();
									errors.forEach((error) => {
										fireErrorAnalytics({
											meta: {
												id: 'mutateBatchWindowPreferenceCompletedError',
												packageName: 'jiraPersonalSettingsPreferenceFields',
												teamName: 'jira-notifications-platform',
											},
											error: new Error(error?.message || 'Unknown Error'),
											sendToPrivacyUnsafeSplunk: true,
										});
									});
								} else {
									fireTrackAnalytics(
										createAnalyticsEvent({}),
										'batchWindow changed',
										'batchWindow',
										{
											value: isEnabled ? 'DEFAULT_BATCHING' : 'NO_BATCHING',
										},
									);
									fireOperationalAnalytics(
										createAnalyticsEvent({}),
										'mutateBatchWindowPreference success',
										{
											value: isEnabled ? 'DEFAULT_BATCHING' : 'NO_BATCHING',
										},
									);
								}
							},
						});
					}
				}}
			/>
		</Inline>
	);
};

const labelHolderStyles = xcss({
	marginTop: 'space.050',
	// gap: 'space.100',
});

const labelStyles = xcss({
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	color: 'color.text',
});

const infoIconStyles = xcss({
	marginLeft: 'space.100',
	marginTop: 'space.025',
});

const infoDialogStyles = xcss({
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
	paddingRight: 'space.300',
	paddingLeft: 'space.300',
	maxWidth: '575px',
});
